import { buildAuthedHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'

// Status / Source / Done / Error — gleiche Form wie das Backend in
// services/research/types.ts ausgibt.
export type ResearchStreamEvent =
  | {
      type: 'status'
      phase: string
      detail?: string
      progress?: { done: number; total: number }
      sources?: ResearchSourceSnapshot[]
      source_counts?: Partial<Record<string, number>>
    }
  | { type: 'source'; source: ResearchSourceSnapshot }
  | {
      type: 'done'
      dossier_id: string
      dossier_path: string
      key_findings: string[]
      tier_counts: { tier_1: number; tier_2: number; tier_3: number }
    }
  | { type: 'error'; message: string }

export interface ResearchSourceSnapshot {
  source_type: string
  title: string
  url: string
  tier: 1 | 2 | 3
  date?: string
  author?: string
}

export interface RunResearchOptions {
  topic: string
  sources?: string[]
  modules?: string[]
  depth?: 'quick' | 'standard' | 'deep'
  language?: 'de' | 'en'
  discovery?: 'tavily' | 'gemini' | 'grok' | 'parallel'
  brand?: string
  sessionId?: string
  signal?: AbortSignal
  onEvent: (event: ResearchStreamEvent) => void
}

export async function streamResearch(opts: RunResearchOptions): Promise<void> {
  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')

  const response = await fetch(`${baseUrl}/api/research`, {
    method: 'POST',
    headers: await buildAuthedHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    }),
    body: JSON.stringify({
      topic: opts.topic,
      sources: opts.sources,
      modules: opts.modules,
      depth: opts.depth,
      language: opts.language,
      discovery: opts.discovery,
      brand: opts.brand,
      session_id: opts.sessionId,
    }),
    signal: opts.signal,
  })

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = (await response.json()) as { error?: { message?: string } }
      message = body.error?.message ?? message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  if (!response.body) throw new Error('Keine Stream-Antwort')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let separatorIdx = buffer.indexOf('\n\n')
      while (separatorIdx !== -1) {
        const rawEvent = buffer.slice(0, separatorIdx)
        buffer = buffer.slice(separatorIdx + 2)

        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue
          const payload = line.slice(5).trim()
          if (!payload) continue
          try {
            const parsed = JSON.parse(payload) as ResearchStreamEvent
            opts.onEvent(parsed)
          } catch (err) {
            console.error('SSE parse error', err, payload)
          }
        }

        separatorIdx = buffer.indexOf('\n\n')
      }
    }
  } finally {
    reader.releaseLock()
  }
}
